import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import JobCard from "./JobCard.jsx";

function minutesSinceMidnight(value) {
  const date = new Date(value);

  return date.getHours() * 60 + date.getMinutes();
}

export default function Timeline({
  jobs,
  currentUserId,
  canControlAnyTimer = false,
  highlightedJobId,
  onHighlightComplete,
  onOpenJob,
  onToggleTimer,
}) {
  const [now, setNow] = useState(new Date());
  const [lineTop, setLineTop] = useState(0);
  const [railHeight, setRailHeight] = useState(0);
  const jobsRef = useRef(null);
  const jobRefs = useRef(new Map());

  const updateTimelineLayout = useCallback(() => {
    const jobsElement = jobsRef.current;

    if (!jobsElement) {
      return;
    }

    const containerTop = jobsElement.getBoundingClientRect().top;
    const currentMinutes = minutesSinceMidnight(now);
    const entries = jobs
      .map((job) => {
        const element = jobRefs.current.get(job.id);

        if (!element) {
          return null;
        }

        const bounds = element.getBoundingClientRect();

        return {
          start: minutesSinceMidnight(job.scheduledStart),
          end: minutesSinceMidnight(job.scheduledEnd),
          top: bounds.top - containerTop,
          bottom: bounds.bottom - containerTop,
        };
      })
      .filter(Boolean);

    let nextLineTop = Math.max(24, jobsElement.scrollHeight / 2);

    if (entries.length > 0) {
      const activeEntries = entries.filter((entry) => entry.start <= currentMinutes && currentMinutes <= entry.end);

      if (activeEntries.length > 0) {
        const activeEntry = activeEntries[activeEntries.length - 1];
        const duration = Math.max(1, activeEntry.end - activeEntry.start);
        const progress = Math.min(1, Math.max(0, (currentMinutes - activeEntry.start) / duration));

        nextLineTop = activeEntry.top + (activeEntry.bottom - activeEntry.top) * progress;
      } else {
        const completedEntries = entries.filter((entry) => entry.end < currentMinutes);
        const upcomingEntries = entries.filter((entry) => entry.start > currentMinutes);
        const previousEntry = completedEntries.at(-1);
        const nextEntry = upcomingEntries[0];

        if (previousEntry && nextEntry) {
          const gap = Math.max(1, nextEntry.start - previousEntry.end);
          const progress = Math.min(1, Math.max(0, (currentMinutes - previousEntry.end) / gap));

          nextLineTop = previousEntry.bottom + (nextEntry.top - previousEntry.bottom) * progress;
        } else if (previousEntry) {
          const minutesAfter = currentMinutes - previousEntry.end;

          nextLineTop = previousEntry.bottom + Math.min(120, minutesAfter);
        } else if (nextEntry) {
          const minutesBefore = nextEntry.start - currentMinutes;

          nextLineTop = Math.max(24, nextEntry.top - Math.min(120, minutesBefore));
        }
      }
    }

    setLineTop(nextLineTop);
    setRailHeight(Math.max(jobsElement.scrollHeight, nextLineTop + 32));
  }, [jobs, now]);

  useEffect(() => {
    const updateAtNextMinute = () => {
      const delay = 60000 - (Date.now() % 60000);

      return window.setTimeout(() => {
        setNow(new Date());
        timerId = updateAtNextMinute();
      }, delay);
    };

    let timerId = updateAtNextMinute();

    return () => window.clearTimeout(timerId);
  }, []);

  useLayoutEffect(() => {
    updateTimelineLayout();

    const resizeObserver = new ResizeObserver(updateTimelineLayout);

    if (jobsRef.current) {
      resizeObserver.observe(jobsRef.current);
    }

    window.addEventListener("resize", updateTimelineLayout);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateTimelineLayout);
    };
  }, [updateTimelineLayout]);

  useEffect(() => {
    const highlightedElement = jobRefs.current.get(highlightedJobId);

    if (!highlightedElement) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      highlightedElement.scrollIntoView({ behavior: "smooth", block: "center" });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [highlightedJobId, jobs]);

  return (
    <div className="timeline">
      <div className="timeline-rail" style={{ minHeight: `${railHeight}px` }}>
        <div className="now-line" style={{ top: `${lineTop}px` }}>
          <span>{new Intl.DateTimeFormat("en", { hour: "numeric", minute: "2-digit" }).format(now)}</span>
        </div>
      </div>
      <div className="timeline-jobs" ref={jobsRef}>
        {jobs.map((job) => (
          <div
            className={`timeline-job-slot ${job.id === highlightedJobId ? "timeline-job-highlighted" : ""}`}
            key={job.id}
            ref={(element) => {
              if (element) {
                jobRefs.current.set(job.id, element);
              } else {
                jobRefs.current.delete(job.id);
              }
            }}
            onAnimationEnd={(event) => {
              if (event.animationName === "schedule-occurrence-highlight" && job.id === highlightedJobId) {
                onHighlightComplete?.();
              }
            }}
          >
            <JobCard
              job={job}
              currentUserId={currentUserId}
              canControlAnyTimer={canControlAnyTimer}
              onOpen={onOpenJob}
              onToggleTimer={onToggleTimer}
            />
          </div>
        ))}
        {jobs.length === 0 && <div className="empty-state">No jobs scheduled for this day.</div>}
      </div>
    </div>
  );
}
